import { PaginationCardMinimal } from "@admin-ui/components/application/pagination/pagination";
import { TableEmptyState } from "@admin-ui/components/composed/table-empty-state";
import { ConfirmationDialog } from "@admin-ui/components/primitives/application/confirmation-dialog";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { formatDate } from "@admin-ui/utils/format-date";
import { toSortDescriptor } from "@admin-ui/utils/sort";
import { adminAccountPath, adminDestinationPath, adminLocationsPath, adminProgramPath } from "@travily/routes";
import type { AdminProgramsProgram } from "@travily/types/models";
import type AdminProgramsIndex from "@travily/types/pages/AdminProgramsIndex";
import { BookOpen01, Edit05, EyeOff, SearchLg, Send01, Trash01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import type { SortDescriptor } from "react-aria-components";
import { Link, TableBody } from "react-aria-components";

export type ProgramFilters = AdminProgramsIndex["filters"];
export type ProgramPagy = AdminProgramsIndex["pagy"];

export interface IndexViewProps {
  programs: AdminProgramsProgram[];
  pagy: ProgramPagy;
  sort: string | null;
  sortDir: string | null;
  filters: ProgramFilters;
  onSortChange: (descriptor: SortDescriptor) => void;
  onPageChange: (page: number) => void;
  onSearchChange: (value: string) => void;
  onEditProgram: (programId: number) => void;
  onDeleteProgram: (programId: number) => void;
  onPublishProgram: (programId: number) => void;
  onUnpublishProgram: (programId: number) => void;
}

export function IndexView({
  programs,
  pagy,
  sort,
  sortDir,
  filters,
  onSortChange,
  onPageChange,
  onSearchChange,
  onEditProgram,
  onDeleteProgram,
  onPublishProgram,
  onUnpublishProgram,
}: IndexViewProps) {
  const sortDescriptor = toSortDescriptor(sort, sortDir);

  const [searchDraft, setSearchDraft] = useState(filters.search);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const pendingProgram = pendingDeleteId ? programs.find((p) => p.id === pendingDeleteId) : null;

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (searchDraft === filters.search) return;
    const timer = setTimeout(() => onSearchChange(searchDraft), 300);
    return () => clearTimeout(timer);
  }, [searchDraft, filters.search, onSearchChange]);

  return (
    <>
      <div className="flex flex-col gap-3 rounded-xl bg-secondary p-4 ring-1 ring-secondary ring-inset md:flex-row md:flex-wrap md:items-end md:p-5">
        <div className="md:min-w-64 md:flex-1">
          <Input
            aria-label="Search programs"
            placeholder="Search by program name or account handle"
            icon={SearchLg}
            value={searchDraft}
            onChange={setSearchDraft}
            size="sm"
          />
        </div>
      </div>

      <TableCard.Root size="sm">
        <Table aria-label="Programs" sortDescriptor={sortDescriptor} onSortChange={onSortChange}>
          <Table.Header>
            <Table.Head id="name" label="Name" isRowHeader allowsSorting className="w-full max-w-1/3" />
            <Table.Head id="status" label="Status" />
            <Table.Head id="account" label="Account" />
            <Table.Head id="location" label="Location" />
            <Table.Head id="destination" label="Destination" />
            <Table.Head id="created_at" label="Created" allowsSorting />
            <Table.Head id="actions" label="" />
          </Table.Header>
          <TableBody items={programs} renderEmptyState={() => <EmptyState />}>
            {(program: AdminProgramsProgram) => (
              <Table.Row key={program.id} id={program.id}>
                <Table.Cell>
                  <Link
                    href={adminProgramPath(program.id)}
                    className="font-medium text-brand-600 text-sm hover:text-brand-700"
                  >
                    {program.name ?? "Untitled program"}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <Badge color={program.status === "published" ? "success" : "gray"} size="sm">
                    {program.status === "published" ? "Published" : "Draft"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Link
                    href={adminAccountPath(program.account.id)}
                    className="text-brand-600 text-sm hover:text-brand-700"
                  >
                    {program.account.display_name ?? `@${program.account.handle ?? "?"}`}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  {program.primary_location ? (
                    <Link href={adminLocationsPath()} className="text-brand-600 text-sm hover:text-brand-700">
                      {program.primary_location.name}
                      {program.locations_count > 1 && (
                        <Badge color="gray" size="sm" className="ml-2">
                          +{program.locations_count - 1}
                        </Badge>
                      )}
                    </Link>
                  ) : (
                    <span className="text-sm text-tertiary">-</span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {program.primary_destination ? (
                    <Link
                      href={adminDestinationPath(program.primary_destination.id)}
                      className="text-brand-600 text-sm hover:text-brand-700"
                    >
                      {program.primary_destination.name}
                    </Link>
                  ) : (
                    <span className="text-sm text-tertiary">-</span>
                  )}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">{formatDate(program.created_at)}</Table.Cell>
                <Table.Cell>
                  <Dropdown.Root>
                    <Dropdown.DotsButton />
                    <Dropdown.Popover className="w-min">
                      <Dropdown.Menu
                        onAction={(key) => {
                          if (key === "edit") onEditProgram(program.id);
                          if (key === "publish") onPublishProgram(program.id);
                          if (key === "unpublish") onUnpublishProgram(program.id);
                          if (key === "delete") setPendingDeleteId(program.id);
                        }}
                      >
                        <Dropdown.Item id="edit" icon={Edit05} label="Edit" />
                        {program.status === "draft" ? (
                          <Dropdown.Item id="publish" icon={Send01} label="Publish" />
                        ) : (
                          <Dropdown.Item id="unpublish" icon={EyeOff} label="Unpublish" />
                        )}
                        <Dropdown.Item id="delete" icon={Trash01} label="Delete" />
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown.Root>
                </Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      </TableCard.Root>

      <PaginationCardMinimal align="right" page={pagy.page} total={pagy.pages} onPageChange={onPageChange} />

      {pendingProgram && (
        <ConfirmationDialog
          title="Delete program"
          description={`This will permanently delete "${pendingProgram.name ?? "Untitled program"}" and remove all its photos and location links. This action cannot be undone.`}
          confirmLabel="Delete"
          color="error"
          isOpen
          onOpenChange={(open) => {
            if (!open) setPendingDeleteId(null);
          }}
          onConfirm={() => {
            onDeleteProgram(pendingProgram.id);
            setPendingDeleteId(null);
          }}
        />
      )}
    </>
  );
}

function EmptyState() {
  return <TableEmptyState icon={BookOpen01} title="No programs match" description="Try clearing the search." />;
}
